import { UiSchema } from '@rjsf/core';
import { JSONSchema7 } from 'json-schema';
import { pick } from 'lodash';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { IntakeForm } from 'views/pages/client-record/useIntakeForm';
import JsonFormContainer from '../JsonFormContainer';
import TableRowTemplate from '../templates/TableRowTemplate';
import TableTemplate from '../templates/TableTemplate';
import Actions from './Actions';

const uiSchema: UiSchema = {
  'ui:ObjectFieldTemplate': TableTemplate,
  'ui:headers': ['Difficulty', 'Yes / No', 'Family Member'],
  familyHistory_1: {
    'ui:ObjectFieldTemplate': TableRowTemplate,
    familyHistory_1_1: { 'ui:widget': 'radio', 'ui:options': { inline: true } }
  },
  familyHistory_2: {
    'ui:ObjectFieldTemplate': TableRowTemplate,
    familyHistory_2_1: { 'ui:widget': 'radio', 'ui:options': { inline: true } }
  },
  familyHistory_3: {
    'ui:ObjectFieldTemplate': TableRowTemplate,
    familyHistory_3_1: { 'ui:widget': 'radio', 'ui:options': { inline: true } }
  },
  familyHistory_4: {
    'ui:ObjectFieldTemplate': TableRowTemplate,
    familyHistory_4_1: { 'ui:widget': 'radio', 'ui:options': { inline: true } }
  },
  familyHistory_5: {
    'ui:ObjectFieldTemplate': TableRowTemplate,
    familyHistory_5_1: { 'ui:widget': 'radio', 'ui:options': { inline: true } }
  },
  familyHistory_6: {
    'ui:ObjectFieldTemplate': TableRowTemplate,
    familyHistory_6_1: { 'ui:widget': 'radio', 'ui:options': { inline: true } }
  },
  familyHistory_7: {
    'ui:ObjectFieldTemplate': TableRowTemplate,
    familyHistory_7_1: { 'ui:widget': 'radio', 'ui:options': { inline: true } }
  },
  familyHistory_8: {
    'ui:ObjectFieldTemplate': TableRowTemplate,
    familyHistory_8_1: { 'ui:widget': 'radio', 'ui:options': { inline: true } }
  },
  familyHistory_9: {
    'ui:ObjectFieldTemplate': TableRowTemplate,
    familyHistory_9_1: { 'ui:widget': 'radio', 'ui:options': { inline: true } }
  },
  familyHistory_10: {
    'ui:ObjectFieldTemplate': TableRowTemplate,
    familyHistory_10_1: { 'ui:widget': 'radio', 'ui:options': { inline: true } }
  },
  familyHistory_11: {
    'ui:ObjectFieldTemplate': TableRowTemplate,
    familyHistory_11_1: { 'ui:widget': 'radio', 'ui:options': { inline: true } }
  }
};

interface Props {
  intakeForm: IntakeForm;
  onPrevious?: () => void;
  isSubmitting: boolean;
  onSubmit: (data: any) => void;
  handleStep?: any;
}

export default function FamilyMentalHealthHistory({ intakeForm, onPrevious, isSubmitting, onSubmit, handleStep }: Props) {
  const intl = useIntl();
  const schema: JSONSchema7 = {
    type: 'object',
    title: intl.formatMessage({ defaultMessage: 'Family Mental Health History' }),
    description: intl.formatMessage({
      defaultMessage:
        'Has anyone in your family (either immediate family members or relatives) experienced difficulties with the following? (circle any that apply and list family member, e.g. sibling parent, uncle, etc.)'
    }),
    properties: {
      familyHistory_1: {
        type: 'object',
        title: intl.formatMessage({ defaultMessage: 'Depression' }),
        properties: {
          familyHistory_1_1: {
            type: 'string',
            title: '',
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
          },
          familyHistory_1_2: {
            type: 'string',
            title: ''
          }
        }
      },
      familyHistory_2: {
        type: 'object',
        title: intl.formatMessage({ defaultMessage: 'Bipolar disorder' }),
        properties: {
          familyHistory_2_1: {
            type: 'string',
            title: '',
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
          },
          familyHistory_2_2: {
            type: 'string',
            title: ''
          }
        }
      },
      familyHistory_3: {
        type: 'object',
        title: intl.formatMessage({ defaultMessage: 'Anxiety disorder' }),
        properties: {
          familyHistory_3_1: {
            type: 'string',
            title: '',
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
          },
          familyHistory_3_2: {
            type: 'string',
            title: ''
          }
        }
      },
      familyHistory_4: {
        type: 'object',
        title: intl.formatMessage({ defaultMessage: 'Panic attacks' }),
        properties: {
          familyHistory_4_1: {
            type: 'string',
            title: '',
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
          },
          familyHistory_4_2: {
            type: 'string',
            title: ''
          }
        }
      },
      familyHistory_5: {
        type: 'object',
        title: intl.formatMessage({ defaultMessage: 'Schizophrenia' }),
        properties: {
          familyHistory_5_1: {
            type: 'string',
            title: '',
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
          },
          familyHistory_5_2: {
            type: 'string',
            title: ''
          }
        }
      },
      familyHistory_6: {
        type: 'object',
        title: intl.formatMessage({ defaultMessage: 'Alcohol/substance abuse' }),
        properties: {
          familyHistory_6_1: {
            type: 'string',
            title: '',
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
          },
          familyHistory_6_2: {
            type: 'string',
            title: ''
          }
        }
      },
      familyHistory_7: {
        type: 'object',
        title: intl.formatMessage({ defaultMessage: 'Eating disorders' }),
        properties: {
          familyHistory_7_1: {
            type: 'string',
            title: '',
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
          },
          familyHistory_7_2: {
            type: 'string',
            title: ''
          }
        }
      },
      familyHistory_8: {
        type: 'object',
        title: intl.formatMessage({ defaultMessage: 'Learning disabilities' }),
        properties: {
          familyHistory_8_1: {
            type: 'string',
            title: '',
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
          },
          familyHistory_8_2: {
            type: 'string',
            title: ''
          }
        }
      },
      familyHistory_9: {
        type: 'object',
        title: intl.formatMessage({ defaultMessage: 'Trauma history' }),
        properties: {
          familyHistory_9_1: {
            type: 'string',
            title: '',
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
          },
          familyHistory_9_2: {
            type: 'string',
            title: ''
          }
        }
      },
      familyHistory_10: {
        type: 'object',
        title: intl.formatMessage({ defaultMessage: 'Suicide attempts' }),
        properties: {
          familyHistory_10_1: {
            type: 'string',
            title: '',
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
          },
          familyHistory_10_2: {
            type: 'string',
            title: ''
          }
        }
      },
      familyHistory_11: {
        type: 'object',
        title: intl.formatMessage({ defaultMessage: 'Chronic illness' }),
        properties: {
          familyHistory_11_1: {
            type: 'string',
            title: '',
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
          },
          familyHistory_11_2: {
            type: 'string',
            title: ''
          }
        }
      }
    }
  };
  const [formData, setFormData] = useState({
    familyHistory_1: pick(intakeForm.familyHistory, ['familyHistory_1_1', 'familyHistory_1_2']),
    familyHistory_2: pick(intakeForm.familyHistory, ['familyHistory_2_1', 'familyHistory_2_2']),
    familyHistory_3: pick(intakeForm.familyHistory, ['familyHistory_3_1', 'familyHistory_3_2']),
    familyHistory_4: pick(intakeForm.familyHistory, ['familyHistory_4_1', 'familyHistory_4_2']),
    familyHistory_5: pick(intakeForm.familyHistory, ['familyHistory_5_1', 'familyHistory_5_2']),
    familyHistory_6: pick(intakeForm.familyHistory, ['familyHistory_6_1', 'familyHistory_6_2']),
    familyHistory_7: pick(intakeForm.familyHistory, ['familyHistory_7_1', 'familyHistory_7_2']),
    familyHistory_8: pick(intakeForm.familyHistory, ['familyHistory_8_1', 'familyHistory_8_2']),
    familyHistory_9: pick(intakeForm.familyHistory, ['familyHistory_9_1', 'familyHistory_9_2']),
    familyHistory_10: pick(intakeForm.familyHistory, ['familyHistory_10_1', 'familyHistory_10_2']),
    familyHistory_11: pick(intakeForm.familyHistory, ['familyHistory_11_1', 'familyHistory_11_2'])
  });

  return (
    <JsonFormContainer
      noValidate
      schema={schema}
      uiSchema={uiSchema}
      formData={formData}
      onChange={({ formData }) => {
        handleStep({
          familyHistory: {
            ...formData.familyHistory_1,
            ...formData.familyHistory_2,
            ...formData.familyHistory_3,
            ...formData.familyHistory_4,
            ...formData.familyHistory_5,
            ...formData.familyHistory_6,
            ...formData.familyHistory_7,
            ...formData.familyHistory_8,
            ...formData.familyHistory_9,
            ...formData.familyHistory_10,
            ...formData.familyHistory_11
          }
        });
        setFormData(formData);
      }}
      onSubmit={({ formData }) =>
        onSubmit({
          familyHistory: {
            ...formData.familyHistory_1,
            ...formData.familyHistory_2,
            ...formData.familyHistory_3,
            ...formData.familyHistory_4,
            ...formData.familyHistory_5,
            ...formData.familyHistory_6,
            ...formData.familyHistory_7,
            ...formData.familyHistory_8,
            ...formData.familyHistory_9,
            ...formData.familyHistory_10,
            ...formData.familyHistory_11
          }
        })
      }
    >
      <Actions onPrevious={onPrevious} disabled={isSubmitting} />
    </JsonFormContainer>
  );
}
